import React, { useState } from "react";
import { apiRequest } from "../../helpers/api";
import LeadModal from "./LeadModal";
import TagInput from "./TagInput";
import { fromLocalInput, isEmail, isPhone, leadStatuses, manualSources, sourcePlatforms, statusName, taskTypes } from "./leadShared";

const emptyLead = { name: "", email: "", phone: "", company: "", website: "", source: "Other", platform: "", campaign: "", status: "NEW", assignedToUserId: "", tags: [], message: "" };
const fromLead = (lead) => ({
  ...emptyLead,
  ...Object.fromEntries(Object.keys(emptyLead).map((key) => [key, lead[key] ?? emptyLead[key]])),
  source: manualSources.includes(lead.source) ? lead.source : lead.source || "Other",
  assignedToUserId: lead.assignedToUserId ? "self" : "",
  tags: Array.isArray(lead.tags) ? lead.tags : []
});

const validate = (values) => {
  const errors = {};
  if (!values.name.trim() && !values.email.trim()) errors.name = "Enter a name or an email address.";
  if (values.email.trim() && !isEmail(values.email.trim())) errors.email = "Enter a valid email address.";
  if (values.phone.trim() && !isPhone(values.phone.trim())) errors.phone = "Use digits, spaces, +, - or brackets.";
  if (values.website.trim() && !/^https?:\/\//i.test(values.website.trim())) errors.website = "Start with http:// or https://.";
  return errors;
};

// Create or edit a lead. When creating, an optional first follow-up is scheduled as a task.
const LeadEditor = ({ lead, tagSuggestions = [], onClose, onSaved }) => {
  const editing = Boolean(lead?.id);
  const [values, setValues] = useState(() => editing ? fromLead(lead) : { ...emptyLead });
  const [followUp, setFollowUp] = useState({ dueAt: "", type: "FOLLOW_UP" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const set = (key, value) => {
    const next = { ...values, [key]: value };
    setValues(next);
    if (touched) setErrors(validate(next));
  };

  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true); setServerError("");
    // Social-comment leads keep their source; it cannot be chosen manually.
    const payload = { ...values, assignedToUserId: values.assignedToUserId || null };
    if (!manualSources.includes(payload.source)) delete payload.source;
    try {
      const result = await apiRequest(editing ? `/api/leads/${lead.id}` : "/api/leads", { method: editing ? "PATCH" : "POST", body: JSON.stringify(payload) });
      let saved = result.lead;
      if (!editing && followUp.dueAt) {
        const typeLabel = taskTypes.find(([value]) => value === followUp.type)?.[1] || "Follow-up";
        const taskResult = await apiRequest(`/api/leads/${saved.id}/tasks`, { method: "POST", body: JSON.stringify({ title: `${typeLabel} with ${values.name || values.email}`, type: followUp.type, dueAt: fromLocalInput(followUp.dueAt) }) });
        saved = taskResult.lead || saved;
      }
      onSaved(saved, editing);
    } catch (requestError) {
      setServerError(requestError.message || "Unable to save this lead.");
      setBusy(false);
    }
  };

  const field = (key, label, input, { wide = false, hint } = {}) => <label className={`leads-field${wide ? " is-wide" : ""}${errors[key] ? " has-error" : ""}`}>
    <span>{label}</span>{input}
    {errors[key] ? <small className="leads-field-error" role="alert">{errors[key]}</small> : hint ? <small className="leads-field-hint">{hint}</small> : null}
  </label>;
  const text = (key, props = {}) => <input value={values[key]} onChange={(event) => set(key, event.target.value)} aria-invalid={Boolean(errors[key])} {...props} />;

  return <LeadModal
    as="form" onSubmit={submit} size="lg" busy={busy} onClose={onClose}
    eyebrow={editing ? "Edit contact" : "New contact"} title={editing ? `Edit ${lead.name || lead.email || "lead"}` : "Add lead"}
    description={editing ? "Changes are recorded in this lead's activity history." : "Add a contact by hand. A name or an email address is required."}
    footer={<><button type="button" className="leads-secondary-button" onClick={onClose} disabled={busy}>Cancel</button><button type="submit" className="leads-primary-button" disabled={busy}>{busy ? <><i className="ti ti-loader-2 leads-spin" />Saving…</> : editing ? <><i className="ti ti-device-floppy" />Save changes</> : <><i className="ti ti-user-plus" />Add lead</>}</button></>}
  >
    {serverError && <div className="leads-inline-alert is-error" role="alert"><i className="ti ti-alert-circle" /><span>{serverError}</span></div>}
    <fieldset className="leads-form-section">
      <legend>Contact</legend>
      <div className="leads-form-grid-2">
        {field("name", "Full name", text("name", { maxLength: 255, placeholder: "Jane Cooper", autoComplete: "off" }))}
        {field("email", "Email", text("email", { type: "email", maxLength: 254, placeholder: "jane@company.com" }))}
        {field("phone", "Phone", text("phone", { type: "tel", maxLength: 80, placeholder: "+1 555 010 2030" }))}
        {field("company", "Company", text("company", { maxLength: 255, placeholder: "Company name" }))}
        {field("website", "Website", text("website", { type: "url", maxLength: 1000, placeholder: "https://example.com" }), { wide: true })}
      </div>
    </fieldset>
    <fieldset className="leads-form-section">
      <legend>Pipeline</legend>
      <div className="leads-form-grid-2">
        {field("status", "Status", <select value={values.status} onChange={(event) => set("status", event.target.value)}>{leadStatuses.map((status) => <option key={status} value={status}>{statusName(status)}</option>)}</select>)}
        {field("assignedToUserId", "Owner", <select value={values.assignedToUserId} onChange={(event) => set("assignedToUserId", event.target.value)}><option value="">Unassigned</option><option value="self">Me</option></select>)}
        {field("source", "Source", manualSources.includes(values.source) ? <select value={values.source} onChange={(event) => set("source", event.target.value)}>{manualSources.map((source) => <option key={source}>{source}</option>)}</select> : <input value={values.source} disabled />)}
        {field("platform", "Platform", <select value={values.platform || ""} onChange={(event) => set("platform", event.target.value)}><option value="">Not specified</option>{sourcePlatforms.map((platform) => <option key={platform} value={platform}>{platform.replaceAll("_", " ")}</option>)}</select>)}
        {field("campaign", "Campaign", text("campaign", { maxLength: 255, placeholder: "e.g. Spring launch" }), { wide: true })}
        <div className="leads-field is-wide"><span>Tags</span><TagInput value={values.tags} onChange={(tags) => set("tags", tags)} suggestions={tagSuggestions} /><small className="leads-field-hint">Press Enter or comma to add. Up to 20 tags.</small></div>
      </div>
    </fieldset>
    {!editing && <fieldset className="leads-form-section">
      <legend>First follow-up <em>optional</em></legend>
      <div className="leads-form-grid-2">
        {field("followUpType", "Type", <select value={followUp.type} onChange={(event) => setFollowUp({ ...followUp, type: event.target.value })}>{taskTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>)}
        {field("followUpAt", "Due", <input type="datetime-local" value={followUp.dueAt} onChange={(event) => setFollowUp({ ...followUp, dueAt: event.target.value })} />)}
      </div>
    </fieldset>}
    <fieldset className="leads-form-section">
      <legend>Message</legend>
      {field("message", "What they asked about", <textarea rows="3" maxLength={10000} value={values.message || ""} onChange={(event) => set("message", event.target.value)} placeholder="Context for whoever follows up" />, { wide: true })}
    </fieldset>
  </LeadModal>;
};

export default LeadEditor;
